/**
 * Localhost-only first-run warehouse preview. Does not write production.
 * Set localStorage riverr_dev_warehouse_fresh=0 to see live Bespoke data.
 */

import {
  DEFAULT_BASE_IN,
  DEFAULT_BEAM_IN,
  DEFAULT_CAPACITY_EQ,
  DEFAULT_DEPTH_IN,
  DEFAULT_WIDTH_IN,
  deriveLevels,
  padLevelClears,
} from "../pages/Inventory/WarehouseLocations/slotGeometry";

const KEY = "riverr_dev_warehouse_fresh";
const STATE_KEY = "riverr_dev_warehouse_state";
const EVENT = "riverr-dev-warehouse";

const emptyState = () => ({
  warehouseEnabled: false,
  threePlEnabled: false,
  warehouses: [],
  floor: null,
  blockInboundAtPct: 95,
});

export const emptyDevFloor = (warehouseId = "dev-warehouse") => ({
  warehouseId,
  warehouse: {
    id: warehouseId,
    name: "Warehouse",
    building: { widthFt: 48, depthFt: 30 },
  },
  building: { widthFt: 48, depthFt: 30 },
  zones: [
    {
      id: "dev-zone-fast",
      name: "Fast Pick",
      sequence: 1,
      sortOrder: 1,
      aisles: [],
      aisleCount: 0,
      slotCount: 0,
    },
    {
      id: "dev-zone-reserve",
      name: "Reserve",
      sequence: 2,
      sortOrder: 2,
      aisles: [],
      aisleCount: 0,
      slotCount: 0,
    },
  ],
  areas: [],
  pickPath: [],
  totals: { slots: 0, assigned: 0, aisles: 0, areas: 0, areaQuantity: 0 },
});

const devFillState = (qty, cap, hasSku) => {
  if (!hasSku) return "unassigned";
  if (Number(qty) === 0) return "empty";
  const c = Number(cap) || 0;
  if (c > 0 && Number(qty) / c <= 0.25) return "low";
  return "full";
};

const clearDevSlot = (s) => ({
  ...s,
  sku: null,
  poolId: null,
  quantity: 0,
  productName: null,
  productId: null,
  sellerId: null,
  fill: "unassigned",
});

const recountFloor = (floor) => {
  let slots = 0;
  let assigned = 0;
  const pickPath = [];
  const zones = (floor.zones || []).map((z) => {
    const aisles = (z.aisles || []).map((a) => {
      if (a.label) pickPath.push(a.label);
      const bays = (a.bays || []).map((b) => {
        const nextSlots = (b.slots || []).map((s) => ({
          ...s,
          fill: devFillState(s.quantity, s.capacity, !!(s.sku || s.poolId)),
        }));
        const filled = nextSlots.filter((s) => s.sku || s.poolId).length;
        assigned += filled;
        slots += nextSlots.length;
        const levelCount = a.levelCount || 0;
        return {
          ...b,
          slots: nextSlots,
          filled,
          total: nextSlots.length,
          levelChips: Array.from({ length: levelCount }, (_, i) => {
            const level = levelCount - i;
            const hit = nextSlots.find(
              (x) => x.level === level && (x.position || 1) === 1
            );
            return hit?.fill || "unassigned";
          }),
        };
      });
      const slotCount = bays.reduce((n, b) => n + (b.total || 0), 0);
      const occupied = bays.reduce((n, b) => n + (b.filled || 0), 0);
      return {
        ...a,
        bays,
        slotCount,
        occupied,
        x: a.x ?? null,
        y: a.y ?? null,
        orient: a.orient === "down" ? "down" : "across",
        placed: a.x != null && a.y != null,
      };
    });
    return {
      ...z,
      sortOrder: z.sortOrder ?? z.sequence,
      aisles,
      aisleCount: aisles.length,
      slotCount: aisles.reduce((n, a) => n + (a.slotCount || 0), 0),
    };
  });
  const areas = floor.areas || [];
  return {
    ...floor,
    zones,
    areas,
    building: floor.building || { widthFt: 48, depthFt: 30 },
    pickPath,
    totals: {
      slots,
      assigned,
      aisles: zones.reduce((n, z) => n + (z.aisles || []).length, 0),
      areas: areas.length,
      areaQuantity: areas.reduce((s, a) => s + (Number(a.quantity) || 0), 0),
    },
  };
};

export const findDevSlot = (floor, { slotId = null, code = null } = {}) => {
  const wantId = slotId ? String(slotId) : "";
  const wantCode = code ? String(code).trim() : "";
  for (const zone of floor?.zones || []) {
    for (const aisle of zone.aisles || []) {
      for (const bay of aisle.bays || []) {
        for (const slot of bay.slots || []) {
          if (wantId && slot.slotId === wantId) {
            return { slot, zone, aisle, bay };
          }
          if (wantCode && slot.code === wantCode) {
            return { slot, zone, aisle, bay };
          }
        }
      }
    }
  }
  return null;
};

const walkDevSlots = (floor, fn) => ({
  ...floor,
  zones: (floor.zones || []).map((z) => ({
    ...z,
    aisles: (z.aisles || []).map((a) => ({
      ...a,
      bays: (a.bays || []).map((b) => ({
        ...b,
        slots: (b.slots || []).map(fn),
      })),
    })),
  })),
});

/** Shared write for retail stock + warehouse floor / set-locations. */
export const applyDevPlacements = ({
  warehouseId,
  poolId = null,
  sku = null,
  ownerUid = null,
  productName = null,
  productId = null,
  slots = [],
} = {}) => {
  const id =
    String(poolId || "").trim() ||
    `dev-pool-${ownerUid || "house"}-${sku || "sku"}`;
  const floor = getDevFloor(warehouseId);
  const resolved = [];
  for (const row of slots) {
    const hit = findDevSlot(floor, {
      slotId: row.slotId || null,
      code: row.code || null,
    });
    if (!hit) {
      const err = new Error(
        `Location ${row.code || row.slotId || "—"} not found`
      );
      err.status = 404;
      throw err;
    }
    if (hit.slot.poolId && hit.slot.poolId !== id) {
      const err = new Error(`Location ${hit.slot.code} is already assigned`);
      err.status = 409;
      throw err;
    }
    resolved.push({
      code: hit.slot.code,
      slotId: hit.slot.slotId,
      qty: Math.max(0, Math.floor(Number(row.qty ?? row.quantity) || 0)),
    });
  }
  const qtyByCode = new Map(resolved.map((r) => [r.code, r.qty]));
  const taking = new Set(resolved.map((r) => r.code));
  const next = walkDevSlots(floor, (s) => {
    if (s.poolId === id && !taking.has(s.code)) return clearDevSlot(s);
    if (!taking.has(s.code)) return s;
    return {
      ...s,
      sku: sku || s.sku || null,
      poolId: id,
      quantity: qtyByCode.get(s.code) || 0,
      productName: productName || s.productName || null,
      productId: productId || s.productId || null,
      sellerId: ownerUid || s.sellerId || null,
    };
  });
  setDevWarehouseState({ floor: recountFloor(next) });
  return { ok: true, warehouseId, poolId: id, slots: resolved };
};

export const getDevSlotDetail = (warehouseId, slotId) => {
  const floor = getDevFloor(warehouseId);
  const hit = findDevSlot(floor, { slotId });
  if (!hit) {
    const err = new Error("Slot not found");
    err.status = 404;
    throw err;
  }
  const { slot, zone, aisle } = hit;
  const assignment =
    slot.sku || slot.poolId
      ? {
          id: `${slot.slotId}_${slot.sku || "sku"}`,
          slotId: slot.slotId,
          slotCode: slot.code,
          sku: slot.sku || null,
          poolId: slot.poolId || null,
          quantity: slot.quantity || 0,
          productName: slot.productName || null,
          sellerId: slot.sellerId || null,
          active: true,
        }
      : null;
  return {
    slot: { id: slot.slotId, ...slot },
    aisle,
    zone,
    assignment,
    placard: {
      code: slot.code,
      zoneName: zone?.name || "",
      barcodeText: String(slot.code || "").replace(/-/g, ""),
    },
    slottingHint: null,
    needsReplen: false,
    fill: slot.fill,
  };
};

export const getDevPoolLocations = (warehouseId, poolId) => {
  const locations = [];
  let sku = null;
  let productName = null;
  let onHand = 0;
  const floor = getDevFloor(warehouseId);
  for (const z of floor.zones || []) {
    for (const a of z.aisles || []) {
      for (const b of a.bays || []) {
        for (const s of b.slots || []) {
          if (s.poolId !== poolId) continue;
          locations.push({
            code: s.code,
            qty: Number(s.quantity) || 0,
            slotId: s.slotId,
          });
          sku = s.sku || sku;
          productName = s.productName || productName;
          onHand += Number(s.quantity) || 0;
        }
      }
    }
  }
  return {
    poolId,
    sku,
    productName,
    onHand,
    locationTracked: locations.length > 0,
    locations,
  };
};

export const listDevFloorPlacements = (warehouseId) => {
  const byPool = new Map();
  const bySku = new Map();
  const floor = getDevFloor(warehouseId);
  for (const z of floor.zones || []) {
    for (const a of z.aisles || []) {
      for (const b of a.bays || []) {
        for (const s of b.slots || []) {
          if (!s.poolId && !s.sku) continue;
          const loc = {
            code: s.code,
            qty: Number(s.quantity) || 0,
            slotId: s.slotId,
            poolId: s.poolId || null,
          };
          if (s.poolId) {
            const list = byPool.get(s.poolId) || [];
            list.push(loc);
            byPool.set(s.poolId, list);
          }
          if (s.sku) {
            const list = bySku.get(s.sku) || [];
            list.push(loc);
            bySku.set(s.sku, list);
          }
        }
      }
    }
  }
  return { byPool, bySku };
};

export const getDevFloor = (warehouseId) => {
  const s = getDevWarehouseState();
  const wid = warehouseId || s.warehouses[0]?.id || "dev-warehouse";
  if (s.floor) return recountFloor({ ...s.floor, warehouseId: wid });
  return emptyDevFloor(wid);
};

export const addDevZone = (warehouseId, name) => {
  const floor = getDevFloor(warehouseId);
  const zone = {
    id: `dev-zone-${Date.now()}`,
    name: String(name || "").trim() || "Zone",
    sequence: (floor.zones?.length || 0) + 1,
    sortOrder: (floor.zones?.length || 0) + 1,
    aisles: [],
    aisleCount: 0,
    slotCount: 0,
  };
  const next = recountFloor({
    ...floor,
    zones: [...(floor.zones || []), zone],
  });
  setDevWarehouseState({ floor: next });
  return { zone, floor: next };
};

const slotCode = (label, bay, level, position, perLevel) => {
  const bayStr = String(bay).padStart(2, "0");
  return perLevel > 1
    ? `${label}-${bayStr}-${level}-${position}`
    : `${label}-${bayStr}-${level}`;
};

export const addDevAisle = (warehouseId, data = {}) => {
  let floor = getDevFloor(warehouseId);
  let zoneId = data.zoneId;
  if (!zoneId) {
    const created = addDevZone(warehouseId, data.zoneName);
    floor = created.floor;
    zoneId = created.zone.id;
  }
  const label = String(data.label || "A")
    .toUpperCase()
    .trim() || "A";
  const usedLabels = new Set();
  const usedCodes = new Set();
  for (const z of floor.zones || []) {
    for (const a of z.aisles || []) {
      usedLabels.add(String(a.label || "").toUpperCase());
      for (const b of a.bays || []) {
        for (const s of b.slots || []) {
          if (s.code) usedCodes.add(String(s.code).toUpperCase());
        }
      }
    }
  }
  if (usedLabels.has(label)) {
    throw new Error(`Aisle ${label} already exists`);
  }
  const bayCount = Math.min(30, Math.max(1, Number(data.bayCount) || 6));
  const levelCount = Math.min(8, Math.max(1, Number(data.levelCount) || 4));
  const perLevel = Math.min(8, Math.max(1, Number(data.slotsPerLevel) || 1));
  const cap = Math.max(
    1,
    Number(data.defaultCapacityEq || data.defaultCapacity) || DEFAULT_CAPACITY_EQ
  );
  const derived = deriveLevels({
    baseIn: data.baseIn ?? DEFAULT_BASE_IN,
    beamIn: data.beamIn ?? DEFAULT_BEAM_IN,
    clearHeights: padLevelClears(data.levelClearHeights, levelCount),
  });
  const aisleId = `dev-aisle-${label}-${Date.now()}`;
  const bays = [];
  let slotCount = 0;
  for (let bay = 1; bay <= bayCount; bay++) {
    const slots = [];
    for (let level = levelCount; level >= 1; level--) {
      for (let position = 1; position <= perLevel; position++) {
        const geom = derived.levels[level - 1];
        const code = slotCode(label, bay, level, position, perLevel);
        if (usedCodes.has(String(code).toUpperCase())) {
          throw new Error(`Bin ${code} already exists`);
        }
        usedCodes.add(String(code).toUpperCase());
        slots.push({
          slotId: `${aisleId}-${bay}-${level}-${position}`,
          code,
          level,
          position,
          clearHeightIn: geom.clearHeightIn,
          elevationIn: geom.elevationIn,
          topOfLevelIn: geom.topOfLevelIn,
          maxItemHeightIn: geom.maxItemHeightIn,
          reachBand: geom.reachBand,
          isGoldenZone: geom.isGoldenZone,
          capacityEq: cap,
          widthIn: Number(data.defaultWidthIn) || DEFAULT_WIDTH_IN,
          depthIn: Number(data.defaultDepthIn) || DEFAULT_DEPTH_IN,
          capacity: cap,
          status: "active",
          fill: "unassigned",
          sku: null,
          quantity: 0,
        });
        slotCount += 1;
      }
    }
    bays.push({
      bay,
      code: `${label}-${String(bay).padStart(2, "0")}`,
      filled: 0,
      total: levelCount * perLevel,
      levelChips: Array.from({ length: levelCount }, () => "unassigned"),
      slots,
    });
  }
  const aisle = {
    id: aisleId,
    label,
    sequence: floor.pickPath.length + 1,
    zoneId,
    bayCount,
    levelCount,
    slotsPerLevel: perLevel,
    baseIn: derived.baseIn,
    beamIn: derived.beamIn,
    levelClearHeights: derived.levels.map((l) => l.clearHeightIn),
    levels: derived.levels,
    rackHeightIn: derived.rackHeightIn,
    goldenLevels: derived.goldenLevels,
    defaultCapacityEq: cap,
    defaultWidthIn: Number(data.defaultWidthIn) || DEFAULT_WIDTH_IN,
    defaultDepthIn: Number(data.defaultDepthIn) || DEFAULT_DEPTH_IN,
    levelHeights: derived.levels.map((l) => l.clearHeightIn),
    defaultCapacity: cap,
    x: null,
    y: null,
    orient: "across",
    placed: false,
    occupied: 0,
    bays,
    slotCount,
  };
  const next = recountFloor({
    ...floor,
    zones: floor.zones.map((z) =>
      z.id === zoneId ? { ...z, aisles: [...(z.aisles || []), aisle] } : z
    ),
  });
  setDevWarehouseState({ floor: next });
  return { aisle, floor: next, slotsGenerated: slotCount };
};

/** Home-bin (qty 0) or move — first-run only; does not write production. */
export const assignDevRetailBin = ({
  warehouseId,
  poolId = null,
  sku = null,
  ownerUid = null,
  fromBin = null,
  toBin,
  quantity = 0,
  productName = null,
  productId = null,
} = {}) => {
  const result = applyDevPlacements({
    warehouseId,
    poolId,
    sku,
    ownerUid,
    productName,
    productId,
    slots: [{ code: toBin, qty: quantity }],
  });
  return {
    ok: true,
    locked: false,
    fromBin: fromBin || null,
    toBin,
    poolId: result.poolId,
  };
};

export const removeDevAisle = (warehouseId, aisleId) => {
  const floor = getDevFloor(warehouseId);
  const next = recountFloor({
    ...floor,
    zones: floor.zones.map((z) => ({
      ...z,
      aisles: (z.aisles || []).filter((a) => a.id !== aisleId),
    })),
  });
  setDevWarehouseState({ floor: next });
  return next;
};

export const nextDevAisleLabel = (warehouseId) => {
  const used = new Set();
  for (const z of getDevFloor(warehouseId).zones || []) {
    for (const a of z.aisles || []) {
      used.add(String(a.label || "").toUpperCase());
    }
  }
  for (let i = 0; i < 26; i += 1) {
    const label = String.fromCharCode(65 + i);
    if (!used.has(label)) return label;
  }
  throw new Error("No aisle letters remaining");
};

export const reorderDevZones = (warehouseId, orderedZoneIds) => {
  const floor = getDevFloor(warehouseId);
  const byId = new Map((floor.zones || []).map((z) => [z.id, z]));
  const zones = (orderedZoneIds || [])
    .map((id, i) => {
      const z = byId.get(id);
      return z ? { ...z, sequence: i + 1, sortOrder: i + 1 } : null;
    })
    .filter(Boolean);
  const next = recountFloor({ ...floor, zones });
  setDevWarehouseState({ floor: next });
  return { floor: next };
};

export const patchDevAisle = (warehouseId, aisleId, patch) => {
  const floor = getDevFloor(warehouseId);
  const next = recountFloor({
    ...floor,
    zones: (floor.zones || []).map((z) => ({
      ...z,
      aisles: (z.aisles || []).map((a) =>
        a.id === aisleId ? { ...a, ...patch } : a
      ),
    })),
  });
  setDevWarehouseState({ floor: next });
  return { aisle: { id: aisleId, ...patch }, floor: next };
};

export const upsertDevArea = (warehouseId, area, areaId = null) => {
  const floor = getDevFloor(warehouseId);
  const id = areaId || `dev-area-${Date.now()}`;
  const stores = [
    "receiving",
    "staging",
    "returns",
    "quarantine",
    "damaged",
  ].includes(area.type);
  const codes = {
    receiving: "RECV",
    staging: "STAGE",
    returns: "RET",
    quarantine: "QUAR",
    damaged: "DMG",
    pack: "PACK",
    office: "OFFICE",
    obstacle: "OBS",
  };
  const nextArea = {
    id,
    name: area.name,
    type: area.type,
    code: area.code || codes[area.type] || "AREA",
    storesInventory: stores,
    x: area.x ?? null,
    y: area.y ?? null,
    widthFt: area.widthFt ?? null,
    depthFt: area.depthFt ?? null,
    quantity: area.quantity || 0,
  };
  const areas = areaId
    ? (floor.areas || []).map((a) => (a.id === areaId ? { ...a, ...nextArea } : a))
    : [...(floor.areas || []), nextArea];
  const next = recountFloor({ ...floor, areas });
  setDevWarehouseState({ floor: next });
  return { area: nextArea, floor: next };
};

export const removeDevArea = (warehouseId, areaId) => {
  const floor = getDevFloor(warehouseId);
  const next = recountFloor({
    ...floor,
    areas: (floor.areas || []).filter((a) => a.id !== areaId),
  });
  setDevWarehouseState({ floor: next });
  return { ok: true, floor: next };
};

export const assignDevAreaStock = (warehouseId, areaId, { sku, quantity }) => {
  const floor = getDevFloor(warehouseId);
  const areas = (floor.areas || []).map((a) =>
    a.id === areaId
      ? { ...a, quantity: Number(quantity) || 0, sku: sku || a.sku }
      : a
  );
  const next = recountFloor({ ...floor, areas });
  setDevWarehouseState({ floor: next });
  return { assignment: { areaId, sku, quantity: Number(quantity) || 0 }, floor: next };
};

export const countDevSlots = () =>
  Number(getDevWarehouseState().floor?.totals?.slots) || 0;

export const isLocalDevHost = () => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1";
};

export const isDevWarehouseFresh = () => {
  if (!isLocalDevHost()) return false;
  try {
    return window.localStorage.getItem(KEY) !== "0";
  } catch (_e) {
    return false;
  }
};

export const enableDevWarehouseFresh = () => {
  if (!isLocalDevHost()) return;
  try {
    if (window.localStorage.getItem(KEY) === "0") return;
    window.localStorage.setItem(KEY, "1");
    if (!window.localStorage.getItem(STATE_KEY)) {
      window.localStorage.setItem(STATE_KEY, JSON.stringify(emptyState()));
    }
  } catch (_e) {
    /* ignore */
  }
  notifyDevWarehouseFresh();
};

export const getDevWarehouseState = () => {
  try {
    const raw = window.localStorage.getItem(STATE_KEY);
    if (!raw) return emptyState();
    return { ...emptyState(), ...JSON.parse(raw) };
  } catch (_e) {
    return emptyState();
  }
};

export const setDevWarehouseState = (patch) => {
  const next = { ...getDevWarehouseState(), ...patch };
  try {
    window.localStorage.setItem(STATE_KEY, JSON.stringify(next));
  } catch (_e) {
    /* ignore */
  }
  notifyDevWarehouseFresh();
  return next;
};

export const notifyDevWarehouseFresh = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(EVENT));
};

export const DEV_WAREHOUSE_FRESH_EVENT = EVENT;

export const mockProgramSettings = () => {
  const s = getDevWarehouseState();
  return {
    ok: true,
    warehouseEnabled: s.warehouseEnabled,
    enabled: s.threePlEnabled,
    defaults: { blockInboundAtPct: s.blockInboundAtPct ?? 95 },
    readiness: {
      hasLocation: true,
      hasWarehouse: s.warehouses.length > 0,
      warehouseId: s.warehouses[0]?.id || null,
      warehouseName: s.warehouses[0]?.name || null,
      physicalBinsSet: false,
      physicalBins: countDevSlots(),
      slotCount: countDevSlots(),
      blankFootprintBins: 0,
      floorStarted: countDevSlots() > 0,
      warehouseEnabled: s.warehouseEnabled,
    },
    requireInboundBarcode: false,
    status: { physicalBins: 0, onHandBins: 0, occupancyPct: 0 },
  };
};
